import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  ListItemIcon,
  Typography,
  Divider,
  Button,
  Tooltip,
  Popover,
  MenuItem,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Stack,
} from "@mui/material";
import ColumnFilter from "components/ColumnFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import {
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid";
import { loadExcelJS } from "helpers/loadExcelJS";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Grid from "@mui/material/Grid2";
import { OVERLAY_NAMES } from "constants/Overlays";
import Menu from "@mui/material/Menu";
import { EditRounded } from "@mui/icons-material";
import FileCopyRoundedIcon from "@mui/icons-material/FileCopyRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import {
  normalizePhoneNumber,
  numericComparator,
} from "helpers/ServiceHelpers";
import {
  buildCompactRateDisplayToken,
  buildGroupedRateDisplayToken,
  groupLinguistLanguagesByLanguage,
} from "helpers/linguistRateDisplay";
import api from "services/api";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import { NAVBAR_HEIGHT } from "constants/layout";

const normalizeRequirementValue = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const normalizeLinguistRequirements = (linguist) => {
  if (!linguist) return linguist;

  const requirementMap = { ...(linguist.RequirementMap || {}) };
  const requirementValues = linguist.Requirements || linguist.RequirementValues || [];

  requirementValues.forEach((requirement) => {
    const sourceColumn = requirement?.Definition?.SourceColumn;
    if (sourceColumn) {
      requirementMap[sourceColumn] = normalizeRequirementValue(requirement.Value);
    }
  });

  const normalized = {
    ...linguist,
    RequirementMap: requirementMap,
  };

  Object.entries(requirementMap).forEach(([field, fieldValue]) => {
    normalized[field] = normalizeRequirementValue(fieldValue);
  });

  return normalized;
};

const HOURS_IN_DAY = 24;
const MS_IN_HOUR = 60 * 60 * 1000;
const MS_IN_DAY = HOURS_IN_DAY * MS_IN_HOUR;

const parseDateOnlyToLocalDate = (dateValue) => {
  if (!dateValue || typeof dateValue !== "string") {
    return null;
  }

  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const getElapsedCalendarParts = (startDate, endDate) => {
  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
    const previousMonthLastDay = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      0,
    ).getDate();
    days += previousMonthLastDay;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
  };
};

const formatLastAssignmentAge = (dateValue) => {
  const assignmentDate = parseDateOnlyToLocalDate(dateValue);
  if (!assignmentDate) {
    return "Never";
  }

  const now = new Date();
  const diffMs = now.getTime() - assignmentDate.getTime();

  if (diffMs <= 0) {
    return "0h";
  }

  if (diffMs < MS_IN_DAY) {
    const hours = Math.max(1, Math.floor(diffMs / MS_IN_HOUR));
    return `${hours}h`;
  }

  const days = Math.max(1, Math.floor(diffMs / MS_IN_DAY));

  if (days > 365) {
    const elapsed = getElapsedCalendarParts(assignmentDate, now);
    const safeYears = Math.max(1, elapsed.years);
    return elapsed.months > 0 ? `${safeYears}y ${elapsed.months}mo` : `${safeYears}y`;
  }

  if (days > 30) {
    const elapsed = getElapsedCalendarParts(assignmentDate, now);
    const totalMonths = elapsed.years * 12 + elapsed.months;
    const safeMonths = Math.max(1, totalMonths);
    return elapsed.days > 0 ? `${safeMonths}mo ${elapsed.days}d` : `${safeMonths}mo`;
  }

  return `${days}d`;
};

const compareLastAssignmentDates = (dateA, dateB) => {
  const parsedA = parseDateOnlyToLocalDate(dateA);
  const parsedB = parseDateOnlyToLocalDate(dateB);

  if (!parsedA && !parsedB) return 0;
  if (!parsedA) return 1;
  if (!parsedB) return -1;

  return parsedA.getTime() - parsedB.getTime();
};

// ── Rate cell — compact display (Amine review, 2026-10-06) ─────────────
// The column's valueGetter keeps returning the verbose token
// ("Consecutive $X · Simultaneous $Y") because that string feeds the
// ColumnFilter dropdown options, cross-filter matching, the localStorage
// saved filters, quick search and numericComparator — it must stay stable.
// Only the RENDERED cell is redesigned: split rates show "$30 / $40"
// (consecutive first) and the mode legend moves into a tooltip — desktop
// hovers the rate, mobile taps a small "?". The mobile interaction mirrors
// HourlyRateHoverCell (Members): tap toggles, auto-hides, and the tap is
// swallowed so it never opens the linguist profile (card tap-to-open).
// Single-rate-only rows keep today's behaviour: plain "$X", no tooltip/icon.
const RATE_EXPLAIN_MS = 10000;

const buildRateExplanationLabel = (group) => {
  if (group.hasDistinctSimultaneous) {
    return `Consecutive rate: $${group.baseRate} · Simultaneous rate: $${group.simultaneousRate}`;
  }
  if (group.baseRate === null && group.simultaneousRate !== null) {
    return `Simultaneous rate: $${group.simultaneousRate}`;
  }
  const rate = group.baseRate ?? group.simultaneousRate;
  return rate === null ? null : `$${rate}`;
};

// A legend is only needed when the compact token hides a mode: a distinct
// simultaneous rate, or a simultaneous-only language with no consecutive row.
const needsRateExplanation = (group) =>
  group.hasDistinctSimultaneous ||
  (group.baseRate === null && group.simultaneousRate !== null);

const LinguistRateCell = ({ row }) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const [explainOpen, setExplainOpen] = useState(false);
  const explainTimerRef = useRef(null);

  // Never leave a pending auto-hide timer behind (rows are virtualized).
  useEffect(() => {
    return () => {
      if (explainTimerRef.current) {
        clearTimeout(explainTimerRef.current);
        explainTimerRef.current = null;
      }
    };
  }, []);

  const groups = groupLinguistLanguagesByLanguage(row?.LinguistLanguages);

  // Mirror the valueGetter's Set-dedupe + " | " join, but with compact
  // tokens ("$30 / $40 | $25").
  const compactTokens = Array.from(
    new Set(
      groups
        .map((group) => buildCompactRateDisplayToken(group))
        .filter((token) => token !== null),
    ),
  );
  const display = compactTokens.join(" | ") || "N/A";

  const rateContent = (
    <Typography component="span" variant="body2">
      {display}
    </Typography>
  );

  // Single-rate rows (or no rate): today's behaviour — plain value.
  if (!groups.some((group) => needsRateExplanation(group))) {
    return rateContent;
  }

  const explainedGroups = groups.filter(
    (group) => buildCompactRateDisplayToken(group) !== null,
  );
  const withLanguageNames = explainedGroups.length > 1;
  const tooltipTitle = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {explainedGroups.map((group) => {
        const label = buildRateExplanationLabel(group);
        return (
          <span key={String(group.languageId)}>
            {withLanguageNames && group.name
              ? `${group.name} — ${label}`
              : label}
          </span>
        );
      })}
    </Box>
  );

  // Mobile: no hover — the "?" toggles the legend on tap (auto re-hides),
  // swallowed so the tap never opens the profile / arms selection.
  if (isMobile) {
    const handleExplainTap = (event) => {
      event.stopPropagation();

      if (explainTimerRef.current) {
        clearTimeout(explainTimerRef.current);
        explainTimerRef.current = null;
      }

      if (explainOpen) {
        setExplainOpen(false);
        return;
      }

      setExplainOpen(true);
      explainTimerRef.current = setTimeout(() => {
        explainTimerRef.current = null;
        setExplainOpen(false);
      }, RATE_EXPLAIN_MS);
    };

    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        {rateContent}
        <Tooltip title={tooltipTitle} open={explainOpen} placement="top">
          <Box
            component="span"
            role="button"
            tabIndex={0}
            aria-label="Explain rate modes"
            onClick={handleExplainTap}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
              color: theme.palette.primary.text3,
            }}
          >
            <HelpOutlineRoundedIcon sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  // Desktop: hover over the rate reveals the legend.
  return (
    <Tooltip title={tooltipTitle} placement="top">
      <Box component="span">{rateContent}</Box>
    </Tooltip>
  );
};

const Linguists = () => {
  usePageTitle("Linguists");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const navigate = useNavigate();

  const { setOverlayData, setOverlayName, showSnackbar } = useStateContext();

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("LINGUISTS_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("LINGUISTS_COLUMNS_VISIBLE");
    return saved ? JSON.parse(saved) : {};
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'
  const [overlayAnchorEl, setOverlayAnchorEl] = useState(null);

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("LINGUISTS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "LINGUISTS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  const [isLoading, setIsLoading] = useState(true);

  const [listofLinguists, setListOfLinguists] = useState([]);

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listofLanguages, setListOfLanguages] = useState("");
  const [listofLinguistStatuses, setListOfLinguistStatuses] = useState("");
  const [listofLanguageModes, setListofLanguageModes] = useState("");
  const [requirementDefinitions, setRequirementDefinitions] = useState([]);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  // Round-7 tap-bulletproofing (Amine, 4th on-device report): the long-press
  // ghost-click gate is a TIME WINDOW, not a boolean. A missed clear path on
  // a boolean swallowed the next tap forever; a timestamp expires
  // structurally shortly after the long-press that opened it.
  const suppressClickUntilRef = useRef(0);
  // Stamps the last touchend so the row handlers can tell the synthetic
  // mouse events that follow a touch (mousedown → mouseup → click) apart
  // from a genuine new press.
  const suppressTouchGraceRef = useRef(0);
  const longPressTriggeredRef = useRef(false);
  const LONG_PRESS_MS = 500;
  // Round-5 tap fix (Amine: "I tapped an item card, and it didn't open"):
  // a long-press suppress guard whose ghost click never arrived (Android
  // contextmenu, drag-off, pointercancel) must never swallow the NEXT tap.
  // A mousedown older than this since the last touchend is a real press.
  const GHOST_MOUSE_MS = 1000;
  // Round-7: window for the ghost click that follows a long-press — it
  // lands within a few frames of the release; 700ms covers slow devices
  // and can never block a later, genuine tap.
  const GHOST_CLICK_WINDOW_MS = 700;

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for LinguistsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "LinguistsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "LinguistsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Name" -> "LINGUISTS_FILTER_NAME"
    const upperFieldName = fieldName.toUpperCase();
    return `LINGUISTS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const allFilterFields = useMemo(
    () => [
      "Name",
      "Email",
      "PhoneNumber",
      "Address",
      "Status",
      "LinguistLanguages.Rate",
      "LinguistLanguages.Languages",
      "MinimumHours",
      "LinguistLanguages.InterpretationTypes",
      "Active",
      ...requirementDefinitions
        .filter((definition) => definition?.SourceColumn)
        .map((definition) => definition.SourceColumn),
      "Certifications",
    ],
    [requirementDefinitions],
  );

  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};

    allFilterFields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [allFilterFields, loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    allFilterFields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [allFilterFields, getFilterStorageKey]);

  const LINGUISTS_FILTER_KEY = "LINGUISTS_FILTER_MODEL";

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(LINGUISTS_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    // New-press hygiene: a leftover long-press suppress guard (its ghost
    // click never came) must not swallow this press's click. The synthetic
    // mousedown that follows a touch keeps the guard — its click is still
    // on its way (see suppressTouchGraceRef).
    if (Date.now() - suppressTouchGraceRef.current > GHOST_MOUSE_MS) {
      suppressClickUntilRef.current = 0;
    }
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      longPressTriggeredRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const handleRowTouchStart = (event) => {
    // A new physical touch always starts clean: any earlier ghost click has
    // already fired (or the browser cancels it when a new touch arrives).
    // Without this, a stale guard swallows the very next card tap.
    suppressClickUntilRef.current = 0;
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      longPressTriggeredRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
    suppressTouchGraceRef.current = Date.now();
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = useCallback(() => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressClickUntilRef.current = 0;
  }, []);

  // Auto-disarm (Amine 2026-10-05, round 6): when the ARMED selection model
  // empties through user toggling — the last selected card tapped OFF —
  // selection mode exits so the NEXT tap opens the card normally instead of
  // silently toggling again. The ref only lets the effect disarm after the
  // armed session has SEEN a non-empty model: the long-press arms
  // `selectionEnabled` and appends its id in the same callback, and the ref
  // guard keeps any transitional render (armed + empty model) from disarming
  // instantly.
  const armedSawSelectionRef = useRef(false);
  useEffect(() => {
    if (!selectionEnabled) {
      armedSawSelectionRef.current = false;
      return;
    }
    if (selectionModel.length > 0) {
      armedSawSelectionRef.current = true;
      return;
    }
    if (armedSawSelectionRef.current) {
      armedSawSelectionRef.current = false;
      setSelectionEnabled(false);
    }
  }, [selectionEnabled, selectionModel]);

  const handleMergeLinguists = useCallback(() => {
    if (selectionModel.length < 1) {
      showSnackbar("Select at least 1 linguist to merge.", theme, "error");
      return;
    }

    setOverlayData({
      Linguists: listofLinguists,
      SelectedLinguistIds: selectionModel,
    });

    setOverlayName(OVERLAY_NAMES.LINGUIST_MERGE);
  }, [
    selectionModel,
    showSnackbar,
    theme,
    setOverlayData,
    listofLinguists,
    setOverlayName,
  ]);

  const handleContextMenuEdit = () => {
    const secondData = {
      LinguistID: selectedRow,
      Linguist: listofLinguists.find((linguist) => linguist.id === selectedRow),
      Languages: listofLanguages,
      LinguistStatuses: listofLinguistStatuses,
      LanguageModes: listofLanguageModes,
      Action: OVERLAY_NAMES.LINGUIST_ACTION_EDIT,
    };

    setOverlayData(secondData);

    setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);

    setContextMenu(null);
  };

  const handleContextMenuCall = () => {
    const selectedLinguist = listofLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const phones = selectedLinguist.PhoneNumbers || [];
      const primary = phones.find((p) => p.IsPrimary) || phones[0];
      const phoneNumber = primary?.Number || selectedLinguist.PhoneNumber;
      if (phoneNumber) {
        const dialNumber = phoneNumber.replace(/[^0-9+]/g, "") + (primary?.Ext ? `,,${primary.Ext}` : "");
        window.location.href = `tel:${dialNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendEmail = () => {
    const selectedLinguist = listofLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const email = selectedLinguist.Email;
      if (email) {
        window.open(`mailto:${email}`, "_blank");
      } else {
        showSnackbar("No Email Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendSMS = () => {
    const selectedLinguist = listofLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const phones = selectedLinguist.PhoneNumbers || [];
      const primary = phones.find((p) => p.IsPrimary) || phones[0];
      const phoneNumber = primary?.Number || selectedLinguist.PhoneNumber;
      if (phoneNumber) {
        window.location.href = `sms:${phoneNumber.replace(/[^0-9+]/g, "")}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuOpenMaps = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Address) {
        const address = encodeURIComponent(linguist.Address);
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${address}`,
          "_blank",
        );
      } else {
        showSnackbar("No Address Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyEmail = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Email) {
        navigator.clipboard
          .writeText(linguist.Email)
          .then(() =>
            showSnackbar(
              "Email successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar("Failed to copy email to clipboard.", theme, "error"),
          );
      } else {
        showSnackbar("No Email Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyNumber = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      const phones = linguist.PhoneNumbers || [];
      const allNumbers = phones.length > 0
        ? phones.map((p) => {
            const cc = p.CountryCode && p.CountryCode !== "1" ? `+${p.CountryCode} ` : "";
            const ext = p.Ext ? ` x${p.Ext}` : "";
            const label = p.Label ? ` (${p.Label})` : "";
            return `${cc}${p.Number}${ext}${label}`;
          }).join("\n")
        : linguist.PhoneNumber;
      if (allNumbers) {
        navigator.clipboard
          .writeText(allNumbers)
          .then(() =>
            showSnackbar(
              "Phone number successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy phone number to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyAddress = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Address) {
        navigator.clipboard
          .writeText(linguist.Address)
          .then(() =>
            showSnackbar(
              "Address successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy address to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar("No Address Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuAddTask = () => {
    const linguistData = listofLinguists.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Linguist",
        EntityID: selectedRow,
      },
      Linguist: {
        id: linguistData.id,
        Name: linguistData.Name,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  // Helper functions to check if data is available
  const getSelectedLinguist = () => {
    return listofLinguists.find((linguist) => linguist.id === selectedRow);
  };

  const hasPhoneNumber = () => {
    const linguist = getSelectedLinguist();
    if (linguist?.PhoneNumbers && linguist.PhoneNumbers.length > 0) {
      return linguist.PhoneNumbers.some((p) => p.Number);
    }
    return linguist?.PhoneNumber;
  };

  const hasEmail = () => {
    const linguist = getSelectedLinguist();
    return linguist?.Email;
  };

  const hasAddress = () => {
    const linguist = getSelectedLinguist();
    return linguist?.Address;
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const { refreshPage } = useStateContext();

  // Fetch linguists data
  const fetchLinguistsData = useCallback(() => {
    captureGridScroll();
    api
      .all([
        api.get("/linguists"),
        api.get("/languages"),
        api.get("/linguists/linguistStatuses"),
        api.get("/linguists/languageModes"),
        api.get("/linguists/requirements"),
      ])
      .then(
        api.spread(
          (
            linguistsResponse,
            languagesResponse,
            linguistStatusesResponse,
            languageModesResponse,
            requirementDefinitionsResponse,
          ) => {
            if (linguistsResponse !== null) {
              const linguists = (linguistsResponse.data || []).map(
                normalizeLinguistRequirements,
              );
              setListOfLinguists(linguists);
              restoreGridScroll(linguists);
            }

            if (languagesResponse !== null) {
              setListOfLanguages(languagesResponse.data);
            }

            if (linguistStatusesResponse !== null) {
              setListOfLinguistStatuses(linguistStatusesResponse.data);
            }

            if (languageModesResponse !== null) {
              setListofLanguageModes(languageModesResponse.data);
            }

            if (requirementDefinitionsResponse !== null) {
              setRequirementDefinitions(requirementDefinitionsResponse.data || []);
            }

            setIsLoading(false);
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsDataListsReady(true);
      });
  }, [handleErrorDialog, theme, captureGridScroll, restoreGridScroll]);

  useEffect(() => {
    fetchLinguistsData();
  }, [refreshPage, fetchLinguistsData]);

  useEffect(() => {
    setSelectionModel((prev) =>
      prev.filter((selectedId) =>
        (listofLinguists || []).some((linguist) => linguist.id === selectedId),
      ),
    );
  }, [listofLinguists]);

  // Listen for linguist updates from other members
  const handleLinguistsListRefetch = useCallback(
    (updatedBy) => {
      // Store update info for snackbar (separate effect will show it)
      setLastUpdate(updatedBy);

      // Refetch the data
      fetchLinguistsData();
    },
    [fetchLinguistsData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Linguist updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "LinguistsList",
    recordId: "all",
    onUpdate: handleLinguistsListRefetch,
  });

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      setOverlayData({
        Languages: listofLanguages,
        LinguistStatuses: listofLinguistStatuses,
        LanguageModes: listofLanguageModes,
        Action: OVERLAY_NAMES.LINGUIST_ACTION_NEW,
      });

      setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listofLanguages,
    listofLinguistStatuses,
    listofLanguageModes,
    setOverlayData,
    setOverlayName,
  ]);

  const handleSortModelChange = () => {
    // setSortModel is intentionally controlled through column filter controls
  };

  const getRequirementColumnWidth = (headerName) => {
    const safeHeaderName = String(headerName || "").trim();
    if (safeHeaderName.length <= 12) {
      return 120;
    }

    return safeHeaderName.length * 12;
  };

  const getRequirementDisplayValue = (row, sourceColumn) => {
    const mapValue = normalizeRequirementValue(row?.RequirementMap?.[sourceColumn]);
    if (mapValue === true) return "Yes";
    if (mapValue === false) return "No";

    const directValue = normalizeRequirementValue(row?.[sourceColumn]);
    if (directValue === true) return "Yes";
    if (directValue === false) return "No";

    return "N/A";
  };

  const renderColumnHeader = (params) => (
    <DataGridHeader headerName={params.colDef.headerName}>
      <ColumnFilter
        data={filterDataByField[params.field] || []}
        field={params.field}
        valueGetter={params.colDef.valueGetter}
        selectedValues={columnFilters[params.field] || []}
        onFilterChange={(values) =>
          handleColumnFilterChangeWithStorage(params.field, values)
        }
        onSortChange={(field, direction) => {
          const newSortModel = direction ? [{ field, sort: direction }] : [];
          setSortModel(newSortModel);
          handleSortModelChange(newSortModel);
        }}
        currentSort={sortModel.find((s) => s.field === params.field)?.sort || null}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
      />
    </DataGridHeader>
  );

  const requirementColumns = requirementDefinitions
    .filter((definition) => definition?.SourceColumn)
    .map((definition) => {
      const sourceColumn = definition.SourceColumn;
      const headerName = definition.Name || sourceColumn;

      return {
        field: sourceColumn,
        headerName,
        width: getRequirementColumnWidth(headerName),
        valueGetter: (_, row) => getRequirementDisplayValue(row, sourceColumn),
        renderCell: (params) => {
          const value = params.value;
          const color =
            value === "Yes"
              ? theme.palette.primary.main
              : theme.palette.error.main;
          return <span style={{ color }}>{value}</span>;
        },
        renderHeader: renderColumnHeader,
      };
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- column defs form an init-order cycle (columns -> renderColumnHeader -> columnFilters -> useColumnFilters(columns)); defs are resolved lazily by render-time closures so identity stabilization would require restructuring
  const columns = [
    {
      field: "Name",
      headerName: "Name",
      minWidth: 170,
      flex: 1.7,
      renderHeader: renderColumnHeader,
    },
    {
      field: "PhoneNumber",
      headerName: "Phone Number",
      valueGetter: (_, row) => normalizePhoneNumber(row.PhoneNumber),
      minWidth: 160,
      flex: 1.6,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Email",
      headerName: "Email",
      minWidth: 180,
      flex: 1.8,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Address",
      headerName: "Address",
      minWidth: 350,
      flex: 3.5,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Status",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      renderHeader: renderColumnHeader,
    },
    {
      field: "lastAssignmentDate",
      headerName: "Last Assignment",
      valueGetter: (_, row) => formatLastAssignmentAge(row.lastAssignmentDate),
      sortComparator: (_, __, cellParamsA, cellParamsB) =>
        compareLastAssignmentDates(
          cellParamsA.row.lastAssignmentDate,
          cellParamsB.row.lastAssignmentDate,
        ),
      minWidth: 140,
      flex: 1.2,
      renderHeader: renderColumnHeader,
    },
    {
      field: "LinguistLanguages.Rate",
      headerName: "Rate",
      sortComparator: numericComparator,
      valueGetter: (_, row) => {
        // Verbose token per language (grouped so a language with both a
        // Consecutive and a Simultaneous row lists once):
        // "Consecutive $X · Simultaneous $Y" for split rows, "$X" otherwise.
        // This string feeds the ColumnFilter options, saved filters, quick
        // search and numericComparator — it must stay unchanged. The
        // rendered cell is compact (LinguistRateCell, "$30 / $40" + tooltip).
        const groups = groupLinguistLanguagesByLanguage(row.LinguistLanguages);
        if (groups.length === 0) {
          return "N/A";
        }

        const uniqueItems = new Set(
          groups
            .map((group) => buildGroupedRateDisplayToken(group))
            .filter((token) => token !== null),
        );

        return Array.from(uniqueItems).join(" | ") || "N/A";
      },
      renderCell: (params) => <LinguistRateCell row={params.row} />,
      minWidth: 85,
      flex: 0.85,
      renderHeader: renderColumnHeader,
    },
    {
      field: "LinguistLanguages.Languages",
      headerName: "Languages",
      valueGetter: (_, row) => {
        const data = row.LinguistLanguages;
        if (!data || data.length === 0) {
          return "N/A";
        }

        const uniqueItems = new Set(
          data
            .filter(
              (item) =>
                item.Language.Name !== null && item.Language.Name !== "",
            )
            .map((item) => item.Language.Name),
        );

        return Array.from(uniqueItems).join(" | ") || "N/A";
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: renderColumnHeader,
    },
    {
      field: "MinimumHours",
      headerName: "Minimum Hours",
      valueGetter: (_, row) => row.MinimumHours || "N/A",
      minWidth: 165,
      flex: 1.65,
      renderHeader: renderColumnHeader,
    },
    {
      field: "LinguistLanguages.InterpretationTypes",
      headerName: "Interpretation Types",
      valueGetter: (_, row) => {
        const availabilityFlags = new Set();

        if (row.IsAvailablePhone) {
          availabilityFlags.add("P");
        }
        if (row.IsAvailableVirtual) {
          availabilityFlags.add("V");
        }
        if (row.IsAvailableInPerson) {
          availabilityFlags.add("I");
        }

        return Array.from(availabilityFlags).join(", ") || "N/A";
      },
      minWidth: 200,
      flex: 2,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Active",
      headerName: "Active",
      valueGetter: (_, row) => {
        return row.Active === true
          ? "Yes"
          : row.Active === false
            ? "No"
            : "N/A";
      },
      renderCell: (params) => {
        const value = params.value;
        const color =
          value === "Yes"
            ? theme.palette.primary.main
            : theme.palette.error.main;
        return <span style={{ color }}>{value}</span>;
      },
      minWidth: 100,
      flex: 1,
      renderHeader: renderColumnHeader,
    },
    ...requirementColumns,
    {
      field: "Certifications",
      headerName: "Certification",
      valueGetter: (_, row) => {
        const data = row.Certifications;
        if (!data || data.length === 0) {
          return "N/A";
        }

        return data;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: renderColumnHeader,
    },
  ];

  // Filter columns based on visibility preferences
  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState(getStoredFilterModel);

  // Active / Inactive / All status filter ("all" default)
  const [activeFilter] = useState("all");

  // Apply the Active / Inactive / All status filter before the column-filter
  // pipeline so it composes with column filters and the quick filter.
  const linguistsFilteredByActive = useMemo(() => {
    const source = listofLinguists || [];
    if (activeFilter === "all") return source;
    const wanted = activeFilter === "active";
    return source.filter(
      (linguist) =>
        linguist?.Active === wanted ||
        (activeFilter === "inactive" && linguist?.Active !== true),
    );
  }, [listofLinguists, activeFilter]);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(linguistsFilteredByActive || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Filter columns configuration for cross-filtering
  const filterColumns = columns.map((column) => ({
    field: column.field,
    valueGetter: (row) => {
      if (typeof column.valueGetter === "function") {
        return column.valueGetter(undefined, row);
      }

      return row?.[column.field] || "";
    },
  }));

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listofLinguists || listofLinguists.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listofLinguists, loadAllColumnFilters]);

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();

    const emptyFilterModel = { items: [] };
    setFilterModel(emptyFilterModel);
    localStorage.setItem(
      LINGUISTS_FILTER_KEY,
      JSON.stringify(emptyFilterModel),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Quick filter handling
  const handleFilterModelChange = (newFilterModel) => {
    localStorage.setItem(LINGUISTS_FILTER_KEY, JSON.stringify(newFilterModel));
    setFilterModel(newFilterModel);
  };

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Cross-filtering data calculation
  const filterDataByField = useMemo(() => {
    const filterData = {};

    filterColumns.forEach((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        filterData[column.field] = linguistsFilteredByActive || [];
        return;
      }

      filterData[column.field] = (linguistsFilteredByActive || []).filter(
        (row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(row);
            return selectedValues.includes(value);
          });
        },
      );
    });

    return filterData;
  }, [columnFilters, linguistsFilteredByActive, filterColumns]);

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Helper to get data filtered by both column filters AND quick filter
  const getExportData = useCallback(() => {
    const allColumns = columns || [];
    const visibleColumns =
      apiRef.current?.getVisibleColumns?.() ??
      allColumns.filter((col) => columnsVisible[col.field] !== false);

    if (visibleColumns.length === 0) {
      return { visibleColumns: [], data: [] };
    }

    // Get row IDs that pass ALL filters (column filters + quick filter + sorting)
    // Card mode (Batch C wave 3): no grid is mounted and `useGridApiRef` is
    // `useRef({})` in MUI X v7, so `apiRef.current` is a truthy empty object —
    // guard on its state and fall back to the full column-filtered row set
    // (the desktop path below is unchanged).
    const filteredSortedRowIds = apiRef.current?.state
      ? gridFilteredSortedRowIdsSelector(apiRef)
      : (columnFilteredData || []).map((row) => row.id);
    const rowMap = new Map(
      (columnFilteredData || []).map((row) => [row.id, row]),
    );
    const data = filteredSortedRowIds
      .map((id) => rowMap.get(id))
      .filter(Boolean);

    return { visibleColumns, data };
  }, [columns, columnsVisible, columnFilteredData, apiRef]);

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const { visibleColumns, data: exportData } = getExportData();

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!exportData || exportData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No linguists data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              const csvRows = exportData
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            value = row[col.field];
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");
              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `linguists-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const { visibleColumns, data: exportData } = getExportData();

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!exportData || exportData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No linguists data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              showSnackbar("Preparing XLSX export…", theme, "info");

              // Create workbook and worksheet
              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Linguists");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              exportData.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      value = row[col.field];
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `linguists-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  const openExportDialogRef = useRef(null);
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters,
      onClear,
      theme,
      isMobileToolbar,
      cardMode,
      quickFilterValue,
      onQuickFilterChange,
    }) => {
      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);
      // Mobile selection-actions popover (⋮) — Amine mobile review, 2026-10-01
      const [selectionActionsAnchorEl, setSelectionActionsAnchorEl] =
        useState(null);
      const selectionActionsOpen = Boolean(selectionActionsAnchorEl);

      const handleSelectionActionsClick = (event) => {
        setSelectionActionsAnchorEl(event.currentTarget);
      };

      const handleSelectionActionsClose = () => {
        setSelectionActionsAnchorEl(null);
      };

      const openGenerateSelected = () => {
        if (!selectionModel.length) return;

        const selectedLinguists = (listofLinguists || []).filter((linguist) =>
          selectionModel.includes(linguist.id),
        );

        if (!selectedLinguists.length) return;

        setOverlayData({
          Linguists: selectedLinguists,
          LinguistIDs: selectedLinguists.map((linguist) => linguist.id),
          Linguist: selectedLinguists[0],
          LinguistID: selectedLinguists[0]?.id,
        });
        setOverlayName(OVERLAY_NAMES.LINGUIST_DOCUMENT_GENERATION);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobileToolbar && selectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={openGenerateSelected}
                disabled={selectionModel.length === 0}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Generate Documents
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleMergeLinguists}
                disabled={selectionModel.length === 0}
                startIcon={<MergeRoundedIcon />}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Merge Linguists
              </Button>
            </>
          )}

          {isMobileToolbar && selectionEnabled && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleSelectionActionsClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>

              <Popover
                open={selectionActionsOpen}
                anchorEl={selectionActionsAnchorEl}
                onClose={handleSelectionActionsClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 220 }}>
                  <MenuItem
                    onClick={() => {
                      handleCancelSelection();
                      handleSelectionActionsClose();
                    }}
                  >
                    <ListItemText>Cancel Selection</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      openGenerateSelected();
                      handleSelectionActionsClose();
                    }}
                    disabled={selectionModel.length === 0}
                  >
                    <ListItemText>Generate Documents</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleMergeLinguists();
                      handleSelectionActionsClose();
                    }}
                    disabled={selectionModel.length === 0}
                  >
                    <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                      <MergeRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Merge Linguists</ListItemText>
                  </MenuItem>
                </Box>
              </Popover>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {hasAnyActiveFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && hasAnyActiveFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            {cardMode ? (
              // Card mode: no grid is mounted, so the quick filter must be the
              // context-free QuickFilterField the wrapper drives (CEQuickFilter
              // needs grid context and would throw).
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobileToolbar} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: theme.palette.grey.main,
                  borderColor: theme.palette.divider,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    selectionEnabled,
    selectionModel,
    listofLinguists,
    handleCancelSelection,
    handleMergeLinguists,
    setOverlayData,
    setOverlayName,
  ]);

  // ── Batch C wave 3 — card-mode filter sheet (Linguists) ─────────────────
  // One entry per visible column (every column carries the shared
  // `renderColumnHeader` → ColumnFilter), built from the exact same props as
  // that header renderer (same cross-filter `filterDataByField` source and
  // colDef valueGetter) so filter semantics are identical between modes. In
  // card mode the desktop headers are not rendered, so these sheet copies are
  // the only live filter instances.
  const buildMobileFilterNode = (field) => {
    const colDef = filteredColumns.find((col) => col.field === field);
    return (
      // The header filters keep their trigger at opacity 0 until the column
      // header is hovered — impossible inside a touch sheet, so the sheet copy
      // forces the trigger visible (the desktop headers are untouched).
      <Box sx={{ "& .MuiIconButton-root": { opacity: 1 } }}>
        <ColumnFilter
          data={filterDataByField[field] || []}
          field={field}
          valueGetter={colDef?.valueGetter}
          selectedValues={columnFilters[field] || []}
          onFilterChange={(values) =>
            handleColumnFilterChangeWithStorage(field, values)
          }
          onSortChange={(sortField, direction) => {
            const newSortModel = direction
              ? [{ field: sortField, sort: direction }]
              : [];
            setSortModel(newSortModel);
            handleSortModelChange(newSortModel);
          }}
          currentSort={sortModel.find((s) => s.field === field)?.sort || null}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      </Box>
    );
  };

  const mobileFilterEntries = filteredColumns.map((col) => {
    const selected = columnFilters[col.field] || [];
    return {
      field: col.field,
      headerName: col.headerName || col.field,
      summary: selected.length > 0 ? `${selected.length} selected` : "All",
      active: selected.length > 0,
      node: buildMobileFilterNode(col.field),
    };
  });

  const mobileActiveFilterCount = mobileFilterEntries.filter(
    (entry) => entry.active,
  ).length;

  // Chips row (§3b): one removable chip per active column filter, plus a
  // trailing "Clear all" chip when 2+ filters are active (same semantics as
  // the sheet / toolbar clear). The wrapper adds the search chip itself.
  const mobileFilterChips = mobileFilterEntries
    .filter((entry) => entry.active)
    .map((entry) => ({
      key: `column-filter-${entry.field}`,
      label: `${entry.headerName}: ${columnFilters[entry.field].length} selected`,
      onClear: () => handleColumnFilterChangeWithStorage(entry.field, []),
    }));

  if (mobileFilterChips.length >= 2) {
    mobileFilterChips.push({
      key: "column-filters-clear-all",
      label: "Clear all",
      onClear: handleToolbarClear,
    });
  }

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ pr: 1, gap: 2 }}
          >
            {/* 2026-10-30 — the linguist figures that used to top the old
                Dashboard's Linguists view, one click from the grid they
                describe. Directory-wide totals, not the filtered rows. */}
            <Button
              onClick={() => {
                setOverlayData({});
                setOverlayName(OVERLAY_NAMES.LINGUIST_STATS);
              }}
              startIcon={!isMobile ? <InsightsRoundedIcon /> : undefined}
              sx={{
                width: isMobile ? "40px" : "200px",
                minWidth: isMobile ? "40px" : undefined,
                height: isMobile ? "40px" : undefined,
                display: "flex",
                alignItems: "center",
                justifyContent: isMobile ? "center" : undefined,
                gap: 1,
                padding: isMobile ? 0 : "8px 16px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                // 2026-11-03 (Amine): match the portal's standard elevated
                // button treatment on mobile too — a flat 40px square read as
                // a different component next to the rest of the toolbar.
                border: isMobile
                  ? `1px solid ${theme.palette.primary.border2}`
                  : "none",
                boxShadow: theme.shadows[3],
                color: theme.palette.primary.main,
                textTransform: "none",
                fontSize: "0.875rem",
                "& .MuiButton-startIcon": {
                  marginRight: isMobile ? 0 : undefined,
                },
                "& .MuiButton-endIcon": {
                  display: "none",
                },
                "&:hover": {
                  backgroundColor: theme.palette.primary.buttonHover,
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              {isMobile ? (
                <InsightsRoundedIcon />
              ) : (
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  Linguist Numbers
                </Box>
              )}
            </Button>

            {!isMobile ? (
              <>
                <Button
                  onClick={() => {
                    setOverlayData({ mode: "all" });
                    setOverlayName(OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS);
                  }}
                  startIcon={<FileCopyRoundedIcon />}
                  sx={{
                    width: "220px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>All Documents</Box>
                </Button>

                <Button
                  onClick={() => {
                    setPendingCreateNew(true);
                  }}
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>Add Linguist</Box>
                </Button>
              </>
            ) : (
              <>
                <Tooltip title="All Documents">
                  <IconButton
                    onClick={() => {
                      setOverlayData({ mode: "all" });
                      setOverlayName(OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS);
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      borderRadius: "12px",
                      border: "none",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <FileCopyRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Button
                  onClick={() => {
                    setPendingCreateNew(true);
                  }}
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <AddRoundedIcon />
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          mb: 1,
          mx: 1,
          mt: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: theme.shadows[3],
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGridResponsive
          loading={isLoading}
          mobile={{
            // Card mode (mobile) config — the desktop grid ignores this prop.
            primaryField: "Name",
            badgeField: "Status",
            hiddenFields: [],
            // An address never reads as a same-line label/value — always
            // stacked (explicit beats the 48-char heuristic, deterministic).
            multilineFields: ["Address"],
            // 2026-10-05 (Amine): ONE TAP OPENS the linguist profile on cards
            // (was: tap highlights / double-tap opens). Desktop keeps click =
            // highlight, double-click = open.
            pageSize: 20,
            selectable: true,
            // 2026-10-05 (Amine): mobile has no right-click — the card's
            // top-right ⋮ replays this screen's context menu (same handler,
            // same items and disabled states).
            contextMenuButton: true,
            // Batch C wave 3 — card-mode filter sheet: entries reuse the exact
            // header ColumnFilter props, activeCount drives the toolbar badge,
            // and Clear all reuses the toolbar clear (column filters +
            // quick filter, design §3b).
            filters: mobileFilterEntries,
            activeCount: mobileActiveFilterCount,
            onClearFilters: handleToolbarClear,
            // Removable chips under the control row (the wrapper adds the
            // search chip itself).
            chips: mobileFilterChips,
            // No mobile.footer — Linguists has no totals/summary strip.
          }}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          columns={filteredColumns}
          apiRef={apiRef}
          density={density}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event, details) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            // A long-press opens a 700ms suppression WINDOW synchronously,
            // while the `selectionEnabled` state update may not have rendered
            // yet; the click that follows the long-press must neither toggle
            // selection nor open the profile. Outside the window the gate is
            // structurally closed (round 7: boolean → timestamp + single-
            // shot reset) — a stale guard can never eat a plain tap.
            if (Date.now() < suppressClickUntilRef.current) {
              event.defaultMuiPrevented = true;
              suppressClickUntilRef.current = 0;
              return;
            }

            if (selectionEnabled) {
              // Armed: the wrapper's auto-toggle handles this tap's selection.
              return;
            }

            if (longPressTriggeredRef.current) {
              return;
            }

            // 2026-10-05 (Amine) — ONE TAP OPENS the linguist profile on
            // cards, the same destination as the desktop double-click. The
            // desktop single click keeps its highlight-only selection.
            if (isMobile) {
              const data = {
                Linguist: params.row,
                LinguistID: params.row.id,
              };

              setOverlayData(data);
              navigate(`/linguists/${params.row.id}`, { state: data });
              return;
            }

            setSelectionModel([params.id]);
          }}
          onRowDoubleClick={(params, event, details) => {
            if (openFilter || selectionEnabled || longPressTriggeredRef.current) {
              return;
            }

            const data = {
              Linguist: params.row,
              LinguistID: params.row.id,
            };

            setOverlayData(data);
            navigate(`/linguists/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "✘" || params.value === "N/A"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              showQuickFilter: true,
              hasAnyActiveFilters,
              onClear: handleToolbarClear,
              theme,
              isMobileToolbar: isMobile,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            height: "100%", // Make DataGrid take full height of container
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={handleContextMenuAddTask}>
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Add Task</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCall}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <CallRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Call</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendSMS}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <SmsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send SMS</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuOpenMaps}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <LocationOnRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Open Maps</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCopyEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyNumber}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Number</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyAddress}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Address</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            overlayAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Linguists;
